import type { VoidFunctionComponent } from 'react';
import { useState } from 'react';
import classnames from 'classnames';
import * as moment from 'moment';
import type { RevisionDropdownProps } from './types';
import { Badge, BadgeColors, Icon, icons, Menu, MenuDivider, MenuItem, SearchMenuItem } from '../../../generic';
import dropdownStyles from '../../overview/BranchesDropdown/BranchesDropdown.module.less';
import styles from './Dropdown.module.less';
import { getRevisionMessage, getSourceText } from './utils';
import { compactRevision } from '../../../../helpers/revisions';
import { useProjectRevisions } from '../../../../hooks/api';
import Loader from '../../../generic/Loader';
import type { Revision } from '../../../../types';
import { useRouter } from '../../../../contexts/RouterContext';
import utils from '../../../../utils';
import type { BranchAndTagMap } from '../../../../hooks/api/revisions/useProjectRevisions';
import SourceDropdown from './SourceDropdown';
import { RevisionSource } from '../../../../../typings/enums';
import CommitStatusIcon from './CommitStatusIcon';

type ReferenceType = keyof BranchAndTagMap;

/**
 * Check if a revision should show "Analyzed:" before its commit
 * message. Since CLI revisions always have a timestamp message,
 * we add a "Analyzed:" prefix to clarify the revision value.
 */
const shouldShowAnalyzedLabel = (revision?: Revision) => {
  return revision?.source === RevisionSource.CLI;
};

/**
 * DropdownContent is the content of the dropdown menu.
 * It displays the list of revisions for the selected reference.
 * Clicking on a revision navigates to the revision page.
 */
const DropdownContent: VoidFunctionComponent<{
  project: RevisionDropdownProps['project'];
  reference: RevisionDropdownProps['reference'];
  referenceType: RevisionDropdownProps['referenceType'];
  selectedRevision?: RevisionDropdownProps['selectedRevision'];
  searchText: string;
  source: RevisionSource | null;
}> = ({ project, reference, referenceType, selectedRevision, searchText, source }) => {
  const router = useRouter();
  const projectLocator = project?.data?.locator;
  const { data, isLoading, error } = useProjectRevisions({
    projectLocator: project?.data?.locator,
    refsType: referenceType,
    refName: reference,
    revisionLocatorFilter: searchText,
    source: source || undefined,
  });

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <div className={styles.error}>Something went wrong. Please try again later.</div>;
  }

  let revisions: Revision[] = [];
  const isReferenceInfoAvailable = referenceType && reference;
  if (data && isReferenceInfoAvailable) {
    const references = data[referenceType as ReferenceType] || {};
    revisions = references[reference] || [];
  }

  const navigateToRevision = (revision: Revision) => {
    if (isReferenceInfoAvailable) {
      router.push(
        `/projects/${utils.nuc(projectLocator)}/refs/${utils.nuc(referenceType)}/${utils.nuc(reference)}/${utils.nuc(
          revision.loc.revision,
        )}`,
      );
    }
  };
  const isSBOMProject = project.data.type === 'sbom';

  return (
    <>
      {revisions.map((revision: Revision) => {
        const revisionMessage = getRevisionMessage({
          revision,
          isSelected: false,
          shouldShowAnalyzedLabel: shouldShowAnalyzedLabel(revision),
        });
        const label = compactRevision(revision.locator);
        const sourceText = getSourceText(revision.source);
        const isSelected = selectedRevision?.locator === revision.locator;
        let menuItem = (
          <>
            {label && (
              <Badge color={BadgeColors.LighterGray}>
                <CommitStatusIcon revision={revision} />
                {label}
              </Badge>
            )}
            {revisionMessage}
            {sourceText && <Badge color={BadgeColors.LightGray}>{sourceText}</Badge>}
          </>
        );
        if (isSBOMProject) {
          const revisionTime = moment(revision.revision_timestamp).format('MMM. D YYYY, h:mm a Z');
          menuItem = (
            <>
              <span>{label}</span>
              {revisionTime && (
                <Badge color={BadgeColors.LightGray}>
                  <CommitStatusIcon revision={revision} />
                  {revisionTime}
                </Badge>
              )}
            </>
          );
        }

        return (
          <MenuItem
            key={revision.locator}
            className={classnames(styles.menuItem, {
              [styles.selectedMenuItem]: isSelected,
              [styles.sbomMenuItem]: isSBOMProject,
            })}
            onClick={() => {
              navigateToRevision(revision);
            }}
          >
            {menuItem}
          </MenuItem>
        );
      })}
    </>
  );
};

/**
 * Dropdown is the dropdown menu that displays the selected revision
 * and the list of revisions for the selected reference when expanded.
 */
const Dropdown: VoidFunctionComponent<RevisionDropdownProps> = ({
  selectedRevision,
  project,
  reference,
  referenceType,
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedSource, setSelectedSource] = useState<RevisionSource | null>(null);
  const sourceText = getSourceText(selectedRevision?.source);
  const selectedRevisionMessage = getRevisionMessage({
    revision: selectedRevision,
    isSelected: true,
    shouldShowAnalyzedLabel: shouldShowAnalyzedLabel(selectedRevision),
  });
  const label = compactRevision(selectedRevision?.locator || '');
  const isSBOMProject = project.data.type === 'sbom';
  const isHybridProject = project.data.type === 'provided' && project.data.vcs_host !== null;

  let menuButton = (
    <div className={dropdownStyles.dropdownMenuButton}>
      {label && <Badge color={BadgeColors.DarkGray}>{label}</Badge>}
      <span className={styles.revisionMessage}>
        {selectedRevisionMessage}
      </span>
      {sourceText && (
        <Badge color={BadgeColors.DarkGray}>
          <span className={styles.sourceBadgeText}>{sourceText}</span>
        </Badge>
      )}
      <Icon icon={icons.chevronDown} />
    </div>
  );

  if (isSBOMProject) {
    menuButton = (
      <div className={dropdownStyles.dropdownMenuButton}>
        <Icon icon={icons.listTree} />
        {label}
        <Icon icon={icons.chevronDown} />
      </div>
    );
  }

  return (
    <Menu direction="bottom" menuButton={menuButton}>
      <div className={styles.actions}>
        <SearchMenuItem
          placeholder="Search"
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
          hasBorder
        />
        {isHybridProject && <SourceDropdown project={project} source={selectedSource} onClick={setSelectedSource} />}
      </div>
      <MenuDivider />
      <DropdownContent
        project={project}
        reference={reference}
        referenceType={referenceType}
        selectedRevision={selectedRevision}
        searchText={searchText}
        source={selectedSource}
      />
    </Menu>
  );
};

export default Dropdown;
